import cs from 'classnames';
import { format, parseISO } from 'date-fns';
import { FIXTURE_MATCH_DATE } from '../../constants';
import { ClubNameType } from '../../contracts';
import { Fixture } from '../Fixture/Fixture';
import { LinkSize, LinkVariant } from '../../../common/components/Link/contracts';
import Link from '../../../common/components/Link/Link';
import { RichTextPreview } from '../../../article';
import { Match } from '@repo/types';

export type FixtureListUIProps = {
  fixtures: Match[];
  groupByDate: boolean;
  showAttendance?: boolean;
  groupByCompetition?: boolean;
  competitionsSort?: string[];
  nameType?: ClubNameType;
  className?: string;
  style?: React.CSSProperties;
  columns?: number;
  linkLabel?: string;
  linkUrl?: string;
  linkTargetBlank?: boolean;
  linkVariant?: LinkVariant;
  linkSize?: LinkSize;
  isFixtureExpandable?: boolean;
  title?: string;
  sortByDate?: 'ASC' | 'DESC';
  allFixturesExpanded?: boolean;
};

type FixturesByDate = {
  [date: string]: Match[];
};

function FixtureListUI({
  fixtures,
  nameType,
  columns,
  groupByDate,
  showAttendance = false,
  groupByCompetition = false,
  competitionsSort,
  className,
  style: externalStyle,
  linkLabel,
  linkUrl,
  linkTargetBlank,
  linkVariant = LinkVariant.Outlined,
  linkSize = LinkSize.m,
  isFixtureExpandable,
  title,
  sortByDate,
  allFixturesExpanded,
}: FixtureListUIProps) {
  const style = {
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    ...(externalStyle ?? {}),
  };

  const fixturesByDate: FixturesByDate = fixtures.reduce((acc, fixture) => {
    if (!acc[fixture.date]) {
      acc[fixture.date] = [fixture];
    } else {
      acc[fixture.date]!.push(fixture);
    }

    return acc;
  }, {} as FixturesByDate);

  if (!fixturesByDate) {
    return null;
  }

  if (!groupByDate) {
    return (
      <div className={'fixture-match-list'}>
        {title && <RichTextPreview content={title} />}
        <div className="fixture-match-list__wrapper">
          <div className={'fixture-match-list__matches-container'}>
            <ul
              style={{ ...(style ?? {}), gridTemplateColumns: `repeat(${columns}, 1fr)` }}
              className={cs('fixture-match-list', className)}
            >
              {fixtures
                .sort((a, b) => {
                  // @ts-expect-error TODO: fix this type
                  const dateComparison = new Date(a.date) - new Date(b.date);
                  if (dateComparison !== 0 || !a.time || !b.time) return dateComparison;

                  const timeComparison = a.time.localeCompare(b.time);
                  if (timeComparison !== 0) return timeComparison;

                  const homeTeamComparison = a.homeTeam.fullName.localeCompare(b.homeTeam.fullName);
                  if (homeTeamComparison !== 0) return homeTeamComparison;

                  const awayTeamComparison = a.awayTeam.fullName.localeCompare(b.awayTeam.fullName);
                  return awayTeamComparison;
                })
                .map((fixture) => (
                  <li key={fixture.id} className="fixture-match-list__item">
                    <Fixture
                      isExpanded={allFixturesExpanded}
                      className="fixture-match-list__fixture-match"
                      fixture={fixture}
                      showAttendance={showAttendance}
                      nameType={nameType}
                      isFixtureExpandable={isFixtureExpandable}
                    />
                  </li>
                ))}
            </ul>
          </div>
          {linkLabel && linkUrl && (
            <Link
              className={'fixture-match-list__link'}
              url={linkUrl}
              openInNewTab={!!linkTargetBlank}
              label={linkLabel}
              variant={linkVariant}
              size={linkSize}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={'fixture-match-list'}>
      {title && <RichTextPreview content={title} />}
      <div className={'fixture-match-list__wrapper'}>
        <div className={'fixture-match-list__matches-container'}>
          {Object.entries(fixturesByDate)
            .sort((a, b) => (sortByDate === 'DESC' ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0])))
            .map(([date, fixturesForDate]) => {
              const formattedDate = format(parseISO(date), FIXTURE_MATCH_DATE);

              if (groupByCompetition) {
                const fixturesByCompetition = fixturesForDate.reduce(
                  (acc, fixture) => {
                    if (!acc[fixture.competition.id]) {
                      acc[fixture.competition.id] = {
                        competition: fixture.competition,
                        fixtures: [fixture],
                      };
                    } else {
                      acc[fixture.competition.id]!.fixtures.push(fixture);
                    }

                    return acc;
                  },
                  {} as { [competitionId: string]: { competition: Match['competition']; fixtures: Match[] } }
                );

                return (
                  <div className={cs('fixture-match-list-by-competition', className)} key={date}>
                    <h4 className="fixture-match-list-by-competition__date-title">{formattedDate}</h4>
                    <ul
                      style={style}
                      className={cs(
                        'fixture-match-list-by-competition__competitions-list',
                        'fixture-match-list-by-date__match-list'
                      )}
                    >
                      {Object.keys(fixturesByCompetition)
                        .sort((a, b) => {
                          const firstTeamCompetitionIndex =
                            competitionsSort?.findIndex((competitionId) => a === competitionId) ?? 0;
                          const secondTeamCompetitionIndex =
                            competitionsSort?.findIndex((competitionId) => b === competitionId) ?? 0;

                          return firstTeamCompetitionIndex - secondTeamCompetitionIndex;
                        })
                        .map((competitionId) => {
                          const { competition, fixtures } = fixturesByCompetition[competitionId]!;
                          return (
                            <li key={competitionId}>
                              <h4 className="fixture-match-list-by-competition__competition-title">
                                {competition.name}
                              </h4>
                              <ul>
                                {fixtures
                                  .sort((a, b) => {
                                    // @ts-expect-error TODO: fix this type
                                    const dateComparison = new Date(a.date) - new Date(b.date);
                                    if (dateComparison !== 0 || !a.time || !b.time) return dateComparison;

                                    const timeComparison = a.time.localeCompare(b.time);
                                    if (timeComparison !== 0) return timeComparison;

                                    const homeTeamComparison = a.homeTeam.fullName.localeCompare(b.homeTeam.fullName);
                                    if (homeTeamComparison !== 0) return homeTeamComparison;

                                    const awayTeamComparison = a.awayTeam.fullName.localeCompare(b.awayTeam.fullName);
                                    return awayTeamComparison;
                                  })
                                  .map((fixture) => (
                                    <li key={fixture.id} className="fixture-match-list__item">
                                      <Fixture
                                        isExpanded={allFixturesExpanded}
                                        className="fixture-match-list__fixture-match"
                                        fixture={fixture}
                                        showAttendance={showAttendance}
                                        nameType={nameType}
                                        isFixtureExpandable={isFixtureExpandable}
                                      />
                                    </li>
                                  ))}
                              </ul>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                );
              }

              return (
                <div className={cs('fixture-match-list-by-date', className)} key={date}>
                  <h4 className="fixture-match-list-by-date__date-title">{formattedDate}</h4>
                  <ul style={style} className={cs('fixture-match-list', 'fixture-match-list-by-date__match-list')}>
                    {fixturesForDate
                      .sort((a, b) => {
                        // @ts-expect-error TODO: fix this type
                        const dateComparison = new Date(a.date) - new Date(b.date);
                        if (dateComparison !== 0 || !a.time || !b.time) return dateComparison;

                        const timeComparison = a.time.localeCompare(b.time);
                        if (timeComparison !== 0) return timeComparison;

                        const homeTeamComparison = a.homeTeam.fullName.localeCompare(b.homeTeam.fullName);
                        if (homeTeamComparison !== 0) return homeTeamComparison;

                        const awayTeamComparison = a.awayTeam.fullName.localeCompare(b.awayTeam.fullName);
                        return awayTeamComparison;
                      })
                      .map((fixture) => (
                        <li key={fixture.id} className="fixture-match-list__item">
                          <Fixture
                            isExpanded={allFixturesExpanded}
                            className="fixture-match-list__fixture-match"
                            fixture={fixture}
                            nameType={nameType}
                            isFixtureExpandable={isFixtureExpandable}
                          />
                        </li>
                      ))}
                  </ul>
                </div>
              );
            })}
        </div>
        {linkLabel && linkUrl && (
          <Link
            className={'fixture-match-list__link'}
            url={linkUrl}
            openInNewTab={!!linkTargetBlank}
            label={linkLabel}
            variant={linkVariant}
            size={linkSize}
          />
        )}
      </div>
    </div>
  );
}

export default FixtureListUI;
